import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { TextField, InputAdornment, Box, Container, Typography, CircularProgress, makeStyles } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { http, localstorage } from '../../fx-core';
import * as _ from 'lodash';

// Example styles for the search page layout
const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(12),
        minHeight: '100vh',
    },
    searchBar: {
        marginBottom: theme.spacing(4),
    },
    resultsContainer: {
        marginTop: theme.spacing(3),
    },
    resultSection: {
        marginBottom: theme.spacing(4),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    resultItem: {
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}));

// 1. UPDATED INTERFACE TO INCLUDE ITEMS BY TAGS
interface SearchResult {
    stalls: any[];
    items: any[];
    bookings: any[];
    itemsByCategory: any[];
    itemsByTags: any[]; // <-- ADDED THIS ARRAY
}

const SearchComponent = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { code } = useParams<{ code: string }>();
    const communityId = localstorage.getItem('communityId') || '';
    console.log('Community ID=', communityId);

    const initialQuery = new URLSearchParams(location.search).get('q') || '';
    
    // Set initial state to include the new array, defaulting to empty arrays
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [searchResults, setSearchResults] = useState<SearchResult>({ 
        stalls: [], 
        items: [], 
        bookings: [],
        itemsByCategory: [],
        itemsByTags: [] // <-- INITIALIZED HERE
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    
    
    // --- Data Fetching Logic ---
    
    const performSearch = async (query: string) => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) {
            setSearchResults({ stalls: [], items: [], bookings: [], itemsByCategory: [], itemsByTags: [] }); // <-- UPDATED HERE
            return;
        }

        setIsLoading(true);

        try {
            // MODIFIED: Include communityId in the API call
            let apiEndpoint = `search/search?query=${encodeURIComponent(trimmedQuery)}`;
            
            // Add communityId parameter if it exists
            if (communityId) {
                apiEndpoint += `&communityId=${communityId}`;
            }
            
            const apiOptions: any = {
                url: apiEndpoint,
            };
            
            const res = await http.get(apiOptions); 
            
            const data: SearchResult = res.data; 
            
            // Map all sections, ensuring they default to an empty array if null/undefined
            setSearchResults({
                stalls: data.stalls || [],
                items: data.items || [],
                bookings: data.bookings || [],
                itemsByCategory: data.itemsByCategory || [],
                itemsByTags: data.itemsByTags || [] // <-- MAPPING itemsByTags
            });

        } catch (error) {
            console.error("Search API failed:", error);
            setSearchResults({ stalls: [], items: [], bookings: [], itemsByCategory: [], itemsByTags: [] }); // <-- UPDATED HERE
        } finally {
            setIsLoading(false);
        }
    };

    // Debounced function wrapper for the API call (delay of 500ms)
    const debouncedSearch = useCallback(
        _.debounce((nextQuery: string) => {
             performSearch(nextQuery);
        }, 500),
        []
    );
    
    // Effect to perform search when the searchQuery state changes (live search)
    useEffect(() => {
        if (searchQuery) {
            debouncedSearch(searchQuery);
        } else {
            setSearchResults({ stalls: [], items: [], bookings: [], itemsByCategory: [], itemsByTags: [] }); // <-- UPDATED HERE
            debouncedSearch.cancel();
        }

        return () => {
            debouncedSearch.cancel();
        };
    }, [searchQuery, debouncedSearch]);


    // Effect to sync component state with URL query parameter
    useEffect(() => {
        const queryFromUrl = new URLSearchParams(location.search).get('q') || '';
        if (queryFromUrl !== searchQuery) {
             setSearchQuery(queryFromUrl);
        }
    }, [location.search]); 


    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        setSearchQuery(newQuery);
        history.replace(`${location.pathname}?q=${encodeURIComponent(newQuery)}`);
    };

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        debouncedSearch.cancel();
        performSearch(searchQuery);
    };
 

// --- UTILITY FUNCTION: Slugify (RELIABLE SINGLE-PASS FIX) ---
const slugifyName = (text: string): string => {
    if (!text) return '';
    
   
    let slug = text.toLowerCase().trim();
    
   
    slug = slug.replace(/\s*&\s*/g, '__'); 

    
    slug = slug.replace(/[\s\W]+/g, '-'); 
    
    
    slug = slug.replace(/__/g, '--');
    
    // 4. Final cleanup: collapse any leftover multiple hyphens and trim edges.
    slug = slug.replace(/-{2,}/g, '-');
    slug = slug.replace(/^-+|-+$/g, '');

    return slug;
};
    // --- Navigation Handlers ---
// const handleResultClick = (type: string, item: any) => {
//     console.log(`Clicked ${type}:`, item);
    
//     let targetStallSlug = '';
    
//     if (type === 'stall' && item.slug) {
//         // 1. For the 'stall' result type, use the 'slug' property.
//         targetStallSlug = item.slug;
//     } else if (item.stallName) {
        
//         targetStallSlug =slugifyName(item.stallName);
//     }
//     if (targetStallSlug) {
        
//         history.push(`/${code}/stalls/${targetStallSlug}`);
//     } else {
//         console.warn(`Cannot navigate for ${type}. Missing stall identifier on item:`, item);
//     }
// };

const handleResultClick = async (type: string, item: any) => {
        console.log(`Clicked ${type}:`, item);
        
        // Disable further clicks while processing this one
        if (isNavigating) return;

        let targetStallSlug = item.slug; 
        const stallId = item.stallId; 
        
        // 1. Prioritize direct slug (always present for the 'stall' type)
        if (targetStallSlug) {
            history.push(`/${code}/stalls/${targetStallSlug}`);
            return;
        }

        // 2. Fallback: If slug is missing, but stallId is present (common for items/bookings)
        if (stallId) {
            setIsNavigating(true); // Start loading state
            try {
                // Assuming your API has an endpoint like /portal/stall/{id}
                const apiEndpoint = `/portal/stall/${stallId}`;
                const res = await http.get({ url: apiEndpoint }); 
                
                const fetchedStall = res.data; 
                const newSlug = fetchedStall?.slug;

                if (newSlug) {
                    history.push(`/${code}/stalls/${newSlug}`);
                } else {
                    // Fallback to local slugify if the API didn't return a slug but has the name
                    if (item.stallName) {
                        const generatedSlug = slugifyName(item.stallName);
                        history.push(`/${code}/stalls/${generatedSlug}`);
                    } else {
                        console.error("Failed to load or generate slug for stall ID:", stallId);
                        alert("Error: Could not find stall details for redirection.");
                    }
                }
            } catch (error) {
                console.error("Error fetching stall details:", error);
                alert("Error: Failed to load stall details. Please try again.");
            } finally {
                setIsNavigating(false); // Stop loading state
            }
            return;
        }

        // 3. Final Fallback: If neither slug nor stallId is available, try stallName (last resort)
        if (item.stallName) {
            const generatedSlug = slugifyName(item.stallName);
            history.push(`/${code}/stalls/${generatedSlug}`);
            return;
        }

        console.warn(`Cannot navigate for ${type}. Missing all required identifiers.`, item);
    };

    // Recalculate total results to include the new section
    const totalResults = searchResults.stalls.length + 
                         searchResults.items.length + 
                         searchResults.bookings.length +
                         searchResults.itemsByCategory.length +
                         searchResults.itemsByTags.length; // <-- FINAL TOTAL CALCULATION

    // --- Render Logic ---

    return (
        <Container maxWidth="lg" className={classes.root}>
            <Box className={classes.searchBar}>
                <form onSubmit={handleSearchSubmit}>
                    <TextField
                        fullWidth
                        autoFocus
                        variant="outlined"
                        placeholder="Type to search stalls, items, and services..."
                        value={searchQuery}
                        onChange={handleInputChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </form>
            </Box>

            <Typography variant="h6" gutterBottom>
                {searchQuery ? `Results for "${searchQuery}" (${totalResults})` : "Search the community for stalls, items, or services."}
            </Typography>

            <Box className={classes.resultsContainer}>
                {isLoading && searchQuery ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* No Results Message */}
                        {searchQuery.length > 0 && totalResults === 0 && (
                            <Typography color="textSecondary">No results found for "{searchQuery}" in this community.</Typography>
                        )}

                        {/* Stalls Results */}
                        {searchResults.stalls.length > 0 && (
                            <Box className={classes.resultSection}>
                                <Typography variant="h5" gutterBottom>Stalls ({searchResults.stalls.length})</Typography>
                                {searchResults.stalls.map((stall) => (
                                    <Box 
                                        key={stall.id} 
                                        className={classes.resultItem} 
                                        onClick={() => handleResultClick('stall', stall)}
                                    >
                                        <Typography variant="body1">
                                            <strong>{stall.name}</strong> ({stall.communityName})
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" noWrap>
                                            {stall.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        
                        {/* Items Results (General) */}
                        {searchResults.items.length > 0 && (
                            <Box className={classes.resultSection}>
                                <Typography variant="h5" gutterBottom>Items ({searchResults.items.length})</Typography>
                                {searchResults.items.map((item) => (
                                    <Box 
                                        key={item.id} 
                                        className={classes.resultItem}
                                        onClick={() => handleResultClick('item', item)}
                                    >
                                        <Typography variant="body1">
                                            <strong>{item.itemName}</strong> - {item.stallName} ({item.serviceName})
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" noWrap>
                                            {item.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Bookings/Services Results */}
                        {searchResults.bookings.length > 0 && (
                            <Box className={classes.resultSection}>
                                <Typography variant="h5" gutterBottom>Bookable Services ({searchResults.bookings.length})</Typography>
                                {searchResults.bookings.map((booking) => (
                                    <Box 
                                        key={booking.id} 
                                        className={classes.resultItem}
                                        onClick={() => handleResultClick('booking', booking)}
                                    >
                                        <Typography variant="body1">
                                            <strong>{booking.name}</strong> - {booking.stallName}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" noWrap>
                                            {booking.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Items By Category Results */}
                        {searchResults.itemsByCategory.length > 0 && (
                            <Box className={classes.resultSection}>
                                <Typography variant="h5" gutterBottom>Items by Category ({searchResults.itemsByCategory.length})</Typography>
                                {searchResults.itemsByCategory.map((item) => (
                                    <Box 
                                        key={item.id} 
                                        className={classes.resultItem}
                                        onClick={() => handleResultClick('itemByCategory', item)}
                                    >
                                        <Typography variant="body1">
                                            <strong>{item.itemName}</strong> - {item.stallName} ({item.serviceName})
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" noWrap>
                                            {item.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        
                        {/* 3. NEW SECTION: Items By Tags */}
                        {searchResults.itemsByTags.length > 0 && (
                            <Box className={classes.resultSection}>
                                <Typography variant="h5" gutterBottom>Items by Tags ({searchResults.itemsByTags.length})</Typography>
                                {searchResults.itemsByTags.map((item) => (
                                    <Box 
                                        key={item.id} 
                                        className={classes.resultItem}
                                        onClick={() => handleResultClick('itemByTag', item)}
                                    >
                                        <Typography variant="body1">
                                            <strong>{item.itemName}</strong> - {item.stallName} ({item.serviceName})
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" noWrap>
                                            {item.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        
                        {/* Initial State / Prompt to search */}
                        {searchQuery.length === 0 && totalResults === 0 && !isLoading && (
                            <Typography color="textSecondary">Start typing in the search bar above to find stalls, items, and bookable services.</Typography>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
};

export default SearchComponent;